"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { sendPasswordResetEmail } from "firebase/auth"
import { useTranslations } from "next-intl"
import { Controller, useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { getFirebaseAuth } from "@/lib/auth/getFirebaseAuth"
import {
  type PasswordResetRequestInput,
  passwordResetRequestSchema,
} from "../validators/passwordReset"

type FirebaseAuthError = { code?: string; message?: string }

type PasswordResetErrorKey = "tooManyAttempts" | "network" | "generic"

const getErrorKey = (error: unknown): PasswordResetErrorKey => {
  const code = (error as FirebaseAuthError)?.code
  switch (code) {
    case "auth/too-many-requests":
      return "tooManyAttempts"
    case "auth/network-request-failed":
      return "network"
    default:
      return "generic"
  }
}

export const PasswordResetRequestForm = () => {
  const t = useTranslations("auth.passwordReset")
  const tErrors = useTranslations("auth.passwordReset.errors")

  const form = useForm<PasswordResetRequestInput>({
    resolver: zodResolver(passwordResetRequestSchema),
    defaultValues: { email: "" },
    mode: "onBlur",
  })

  const isSubmitted = form.formState.isSubmitSuccessful

  const onSubmit = form.handleSubmit(async ({ email }) => {
    form.clearErrors("root")
    try {
      // Firebase silently treats unknown emails as successful sends. CC
      // relies on that behaviour for the anti-enumeration message; this
      // matches it.
      await sendPasswordResetEmail(getFirebaseAuth(), email)
    } catch (error) {
      // Network and rate-limit errors are surfaced to the user; everything
      // else (including auth/user-not-found, which Firebase shouldn't throw
      // here but we map to "generic" defensively) maps to the generic
      // error so we don't leak account-existence information.
      const code = (error as FirebaseAuthError)?.code
      if (
        code === "auth/too-many-requests" ||
        code === "auth/network-request-failed"
      ) {
        form.setError("root.serverError", {
          message: tErrors(getErrorKey(error)),
        })
        return
      }
      form.setError("root.serverError", { message: tErrors("generic") })
    }
  })

  const isSubmitting = form.formState.isSubmitting
  const rootError = form.formState.errors.root?.serverError

  if (isSubmitted && !rootError) {
    return (
      <div
        className="flex flex-col gap-2 text-foreground"
        role="status"
        aria-live="polite"
      >
        <p className="text-base font-medium">{t("successTitle")}</p>
        <p className="text-muted-foreground text-sm">{t("successBody")}</p>
      </div>
    )
  }

  return (
    <form onSubmit={onSubmit} noValidate>
      <FieldGroup>
        <Controller
          name="email"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid || undefined}>
              <FieldLabel htmlFor="password-reset-email">
                {t("email.label")}
              </FieldLabel>
              <Input
                {...field}
                id="password-reset-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                enterKeyHint="send"
                placeholder={t("email.placeholder")}
                aria-invalid={fieldState.invalid || undefined}
                disabled={isSubmitting}
              />
              {fieldState.invalid && fieldState.error?.message && (
                <FieldError errors={[{ message: fieldState.error.message }]} />
              )}
            </Field>
          )}
        />
        {rootError?.message && (
          <FieldError errors={[{ message: rootError.message }]} />
        )}
        <Button type="submit" disabled={isSubmitting} className="w-full">
          {isSubmitting ? t("submitting") : t("submit")}
        </Button>
      </FieldGroup>
    </form>
  )
}
